import { useEffect, useState } from 'react'
import { ElementMessageList } from '../../components/mail/ElementMessageList'
import { ElementMessageReader } from '../../components/mail/ElementMessageReader'
import { ElementMessageSearchBar } from '../../components/mail/ElementMessageSearchBar'
import { ElementComposeWindow } from '../../components/compose/ElementComposeWindow'
import { useElementMailStore } from '../../store'
import type { ElementMessage } from '../../types'

export function ElementInboxPage() {
  const {
    messageList,
    activeMessage,
    activeMessageBodyContent,
    activeFolder,
    isMessagesLoading,
    isMessageBodyLoading,
    searchQueryString,
    loadElementMessageList,
    loadElementFolderList,
    openElementMessage,
    markElementMessageRead,
    toggleElementMessageStar,
    deleteElementMessageById,
    setElementSearchQuery,
    clearActiveElementMessage
  } = useElementMailStore()

  const [isComposeWindowOpen, setIsComposeWindowOpen] = useState<boolean>(false)
  const [composeInitialToAddress, setComposeInitialToAddress] = useState<string>('')
  const [composeInitialSubject, setComposeInitialSubject] = useState<string>('')
  const [composeInitialBody, setComposeInitialBody] = useState<string>('')

  useEffect(() => {
    loadElementFolderList()
    loadElementMessageList({ folder: 'INBOX', page: 1 })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    function handleOpenComposeEvent(): void {
      setComposeInitialToAddress('')
      setComposeInitialSubject('')
      setComposeInitialBody('')
      setIsComposeWindowOpen(true)
    }
    globalThis.addEventListener('element:open-compose', handleOpenComposeEvent)
    return () => globalThis.removeEventListener('element:open-compose', handleOpenComposeEvent)
  }, [])

  async function handleMessageSelection(selectedMessage: ElementMessage): Promise<void> {
    await openElementMessage(selectedMessage.id)
  }

  async function handleMarkActiveMessageRead(): Promise<void> {
    if (!activeMessage) return
    await markElementMessageRead(activeMessage.id, true)
  }

  async function handleToggleActiveMessageStar(): Promise<void> {
    if (!activeMessage) return
    const currentlyStarred = activeMessage.is_starred === 1
    await toggleElementMessageStar(activeMessage.id, !currentlyStarred)
  }

  function handleReplyToActiveMessage(): void {
    if (!activeMessage) return
    setComposeInitialToAddress(activeMessage.from_address)
    setComposeInitialSubject(`Re: ${activeMessage.subject || ''}`)
    setComposeInitialBody('')
    setIsComposeWindowOpen(true)
  }

  function handleForwardActiveMessage(): void {
    if (!activeMessage) return
    setComposeInitialToAddress('')
    setComposeInitialSubject(`Fwd: ${activeMessage.subject || ''}`)
    setComposeInitialBody('')
    setIsComposeWindowOpen(true)
  }

  async function handleDeleteActiveMessage(): Promise<void> {
    if (!activeMessage) return
    await deleteElementMessageById(activeMessage.id)
    clearActiveElementMessage()
  }

  async function handleSearchQuerySubmit(submittedSearchQuery: string): Promise<void> {
    await loadElementMessageList({
      folder: activeFolder,
      page: 1,
      search: submittedSearchQuery
    })
  }

  async function handleEmailSentSuccessfully(): Promise<void> {
    await loadElementMessageList({ folder: 'Sent', page: 1 })
  }

  return (
    <div className="element-inbox-page">
      <div className="element-message-list-pane">
        <div className="element-list-pane-header">
          <h2 className="element-active-folder-title">{activeFolder}</h2>
        </div>
        <ElementMessageSearchBar
          currentSearchQuery={searchQueryString}
          onSearchQueryChange={setElementSearchQuery}
          onSearchSubmit={handleSearchQuerySubmit}
        />
        <ElementMessageList
          messageList={messageList}
          activeMessageId={activeMessage?.id || null}
          isMessagesLoading={isMessagesLoading}
          onMessageSelect={handleMessageSelection}
        />
      </div>

      <div className="element-reading-pane">
        <ElementMessageReader
          messageSubject={activeMessage?.subject || null}
          messageSender={activeMessage?.from_address || null}
          messageReceivedAt={activeMessage?.received_at || null}
          messageToAddresses={activeMessage?.to_addresses || null}
          messageBodyContent={activeMessageBodyContent}
          isMessageBodyLoading={isMessageBodyLoading}
          onMarkRead={handleMarkActiveMessageRead}
          onToggleStar={handleToggleActiveMessageStar}
          onReply={handleReplyToActiveMessage}
          onForward={handleForwardActiveMessage}
          onDelete={handleDeleteActiveMessage}
        />
      </div>

      <ElementComposeWindow
        isComposeWindowOpen={isComposeWindowOpen}
        initialToAddress={composeInitialToAddress}
        initialSubjectLine={composeInitialSubject}
        initialBodyContent={composeInitialBody}
        onComposeWindowClose={() => setIsComposeWindowOpen(false)}
        onEmailSentSuccessfully={handleEmailSentSuccessfully}
      />
    </div>
  )
}
